/**
 * A Svelte action: while `el` holds `target` text that is cut off (ellipsis),
 * hovering or focusing it shows `text` in a tooltip (role="tooltip", named by
 * the element's aria-describedby) below it, and leaving or blurring removes
 * it. A native `title` is not shown on keyboard focus, so the tooltip is
 * drawn here. `target` is the element carrying the ellipsis, selected
 * within `el` (default: `el` itself).
 */
let nextId = 0;

export function truncatedTitle(el: HTMLElement, opts: { text: string; target?: string }) {
  let o = opts;
  let tip: HTMLElement | null = null;
  const id = `path-tip-${nextId++}`;

  const hide = () => {
    window.removeEventListener('scroll', hide, true);
    tip?.remove();
    tip = null;
    el.removeAttribute('aria-describedby');
  };
  const show = () => {
    const t = o.target ? el.querySelector<HTMLElement>(o.target) : el;
    if (!t || t.scrollWidth <= t.clientWidth) return hide();
    if (!tip) {
      tip = document.createElement('div');
      tip.id = id;
      tip.className = 'path-tip';
      tip.setAttribute('role', 'tooltip');
      // Moving the pointer from the row onto the tooltip keeps it (to select its text); leaving it ends it.
      tip.addEventListener('pointerleave', (e) => {
        if (!el.contains(e.relatedTarget as Node | null)) hide();
      });
      document.body.append(tip);
      // The row moves when the file list (or the page) scrolls and the tooltip does not follow it.
      window.addEventListener('scroll', hide, true);
      el.setAttribute('aria-describedby', id);
    }
    tip.textContent = o.text;
    const r = el.getBoundingClientRect();
    tip.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - 8 - tip.offsetWidth))}px`;
    tip.style.top = `${r.bottom - 1}px`;
  };
  const onkey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') hide();
  };
  el.addEventListener('pointerenter', show);
  el.addEventListener('focusin', show);
  const leave = (e: PointerEvent) => {
    if (!tip?.contains(e.relatedTarget as Node | null)) hide();
  };
  el.addEventListener('pointerleave', leave);
  el.addEventListener('focusout', hide);
  el.addEventListener('keydown', onkey);
  return {
    update(next: { text: string; target?: string }) {
      o = next;
      if (tip) show();
    },
    destroy() {
      hide();
      el.removeEventListener('pointerenter', show);
      el.removeEventListener('focusin', show);
      el.removeEventListener('pointerleave', leave);
      el.removeEventListener('focusout', hide);
      el.removeEventListener('keydown', onkey);
    },
  };
}
